import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import PDFHeader from './PDFHeader';

const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 11, fontFamily: 'Helvetica', color: '#212529', backgroundColor: '#FFFFFF' },
  container: { width: 520, alignSelf: 'center' },
  detailsBlock: { alignSelf: 'flex-end', width: '60%', marginBottom: 16, backgroundColor: '#F9FAFB', padding: 10, borderRadius: 6, border: '1px solid #E5E7EB' },
  detailsRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 12, marginBottom: 16 },
  card: { flex: 1, border: '1px solid #E5E7EB', borderRadius: 6, padding: 12, backgroundColor: '#FFFFFF' },
  cardTitle: { color: '#1E40AF', fontWeight: 700, fontSize: 12, marginBottom: 8, borderBottom: '1px solid #E5E7EB', paddingBottom: 4 },
  label: { color: '#6B7280', fontSize: 9, marginBottom: 2 },
  value: { fontWeight: 600, fontSize: 10, color: '#1F2937' },
  sectionTitle: { color: '#1E40AF', fontWeight: 700, marginTop: 16, marginBottom: 8, fontSize: 14, borderLeft: '3px solid #2563EB', paddingLeft: 8 },
  divider: { borderTop: '1px solid #E5E7EB', marginVertical: 12 },
  table: { borderTop: '1px solid #E5E7EB', marginTop: 8 },
  tableRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, borderBottom: '1px solid #F3F4F6' },
  tableHeader: { backgroundColor: '#F0F9FF', fontWeight: 700 },
  itemCol: { width: '70%' },
  valueCol: { width: '30%', textAlign: 'right' },
  footer: { marginTop: 24, paddingTop: 12, borderTop: '1px solid #E5E7EB', textAlign: 'center' },
  muted: { color: '#6B7280', fontSize: 9 }
});

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A');
const formatDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A');

export default function TreatmentPDF({ treatment, organization, clinic, currency: currencyProp }) {
  const patient = treatment?.patient || {};
  const doctor = treatment?.user || treatment?.doctor || {};
  const clinicForDocument = treatment?.clinic || treatment?.patient?.registeredClinic || clinic || {};
  // Currency: prop (from org settings) > treatment.organization.settings > INR
  const currency = currencyProp || treatment?.organization?.settings?.currency || 'INR';
  const formatPrice = (price) => {
    if (!price && price !== 0) return 'N/A';
    const formatted = Number(price).toLocaleString('en', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${formatted} ${currency}`;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          {/* Header with Clinic Info */}
          <PDFHeader 
            organization={organization}
            clinic={clinicForDocument}
            documentTitle="Treatment Record"
            documentSubtitle="Patient Treatment Documentation"
          />

          {/* Details block top-right */}
          <View style={styles.detailsBlock}>
            <View style={styles.detailsRow}>
              <Text style={styles.label}>Treatment Code</Text>
              <Text style={{ fontWeight: 600 }}>{treatment?.code || '-'}</Text>
            </View>
            {treatment?.treatmentDate && (
              <View style={styles.detailsRow}>
                <Text style={styles.label}>Treatment Date</Text>
                <Text style={{ fontWeight: 600 }}>{formatDate(treatment.treatmentDate)}</Text>
              </View>
            )}
            {treatment?.treatmentTime && (
              <View style={styles.detailsRow}>
                <Text style={styles.label}>Treatment Time</Text>
                <Text style={{ fontWeight: 600 }}>{treatment.treatmentTime}</Text>
              </View>
            )}
            {treatment?.createdAt && (
              <View style={styles.detailsRow}>
                <Text style={styles.label}>Created</Text>
                <Text style={{ fontWeight: 600 }}>{formatDate(treatment.createdAt)}</Text>
              </View>
            )}
          </View>

          {/* Patient & Treatment Info */}
          <View style={styles.headerRow}>
            {/* Patient Info */}
            <View style={styles.card}>
              <Text style={styles.cardTitle}>Patient Information</Text>
              {patient?.name && <Text style={styles.value}>{patient.name}</Text>}
              {patient?.patientId && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                  ID: {patient.patientId}
                </Text>
              )}
              {patient?.phone && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 4 }}>
                  Phone: {patient.phone}
                </Text>
              )}
              {patient?.email && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                  Email: {patient.email}
                </Text>
              )}
            </View>

            {/* Treatment Info + Doctor / Clinic */}
            <View style={styles.card}>
              <Text style={styles.cardTitle}>Treatment Details</Text>
              <Text style={styles.value}>{treatment?.name || '-'}</Text>
              {treatment?.category && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 4 }}>
                  Category: {treatment.category}
                </Text>
              )}
              {treatment?.duration && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                  Duration: {treatment.duration} minutes
                </Text>
              )}
              {treatment?.price !== undefined && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                  Price: {formatPrice(treatment.price)}
                </Text>
              )}
              {(doctor?.name || doctor?.email) && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 6 }}>
                  Doctor: {doctor.name || 'N/A'}
                  {doctor.email ? ` (${doctor.email})` : ''}
                </Text>
              )}
              {doctor?.licenseNumber && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                  License: {doctor.licenseNumber}
                </Text>
              )}
              {clinicForDocument?.name && (
                <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 2 }}>
                  Clinic: {clinicForDocument.name}
                </Text>
              )}
            </View>
          </View>

          {/* Description */}
          {treatment?.description && (
            <>
              <View style={styles.divider} />
              <Text style={styles.sectionTitle}>Description</Text>
              <Text style={{ fontSize: 10, lineHeight: 1.5 }}>{treatment.description}</Text>
            </>
          )}

          {/* Teeth */}
          {treatment?.teeth && Array.isArray(treatment.teeth) && treatment.teeth.length > 0 && (
            <>
              <View style={styles.divider} />
              <Text style={styles.sectionTitle}>Teeth Involved</Text>
              <Text style={{ fontSize: 10 }}>
                {treatment.teeth.sort((a, b) => a - b).join(', ')}
              </Text>
            </>
          )}

          {/* Required Equipment */}
          {treatment?.requiredEquipment && Array.isArray(treatment.requiredEquipment) && treatment.requiredEquipment.length > 0 && (
            <>
              <View style={styles.divider} />
              <Text style={styles.sectionTitle}>Required Equipment</Text>
              {treatment.requiredEquipment.map((item, idx) => (
                <Text key={idx} style={{ fontSize: 10, marginBottom: 3 }}>
                  • {item}
                </Text>
              ))}
            </>
          )}

          {/* Notes */}
          {treatment?.notes && (
            <>
              <View style={styles.divider} />
              <Text style={styles.sectionTitle}>Notes</Text>
              <Text style={{ fontSize: 10, lineHeight: 1.5 }}>{treatment.notes}</Text>
            </>
          )}

          {/* Treatment Details Table */}
          <View style={styles.divider} />
          <Text style={styles.sectionTitle}>Treatment Information</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.itemCol, { fontWeight: 700 }]}>Field</Text>
              <Text style={[styles.valueCol, { fontWeight: 700 }]}>Value</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={styles.itemCol}>Status</Text>
              <Text style={styles.valueCol}>{treatment?.isActive ? 'Active' : 'Inactive'}</Text>
            </View>
            {treatment?.category && (
              <View style={styles.tableRow}>
                <Text style={styles.itemCol}>Category</Text>
                <Text style={styles.valueCol}>{treatment.category}</Text>
              </View>
            )}
            {treatment?.duration && (
              <View style={styles.tableRow}>
                <Text style={styles.itemCol}>Duration</Text>
                <Text style={styles.valueCol}>{treatment.duration} minutes</Text>
              </View>
            )}
            {treatment?.price !== undefined && (
              <View style={styles.tableRow}>
                <Text style={styles.itemCol}>Price</Text>
                <Text style={styles.valueCol}>{formatPrice(treatment.price)}</Text>
              </View>
            )}
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <Text style={styles.muted}>This treatment record was generated electronically by DentalOS</Text>
            <Text style={[styles.muted, { marginTop: 4 }]}>
              {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })} at {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}
